import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  Divider,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Token as TokenIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Group as GroupIcon,
  Refresh as RefreshIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';

// Import token service (we'll add this to api.ts)
import api from '../services/api';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  tokenBalance: number;
  planName: string;
  monthlyAllocation: number;
  lastActivity: string;
  status: 'active' | 'inactive' | 'suspended';
  joinDate: string;
}

interface TokenTransaction {
  id: number;
  userId: number;
  userName: string;
  type: 'allocation' | 'deduction' | 'bonus' | 'transfer';
  amount: number;
  reason: string;
  adminId: string;
  adminName: string;
  timestamp: string;
  balanceBefore: number;
  balanceAfter: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

// Token service methods using centralized API
const tokenService = {
  getUsersWithTokens: async () => {
    const response = await api.get('/admin/users/tokens');
    return response.data;
  },

  getTokenHistory: async (limit = 50, offset = 0, userId?: number) => {
    const params: any = { limit, offset };
    if (userId) {
      params.userId = userId;
    }
    const response = await api.get('/admin/tokens/history', { params });
    return response.data;
  },

  updateUserTokens: async (userId: number, amount: number, reason: string) => {
    const response = await api.post(`/admin/users/${userId}/tokens`, { amount, reason });
    return response.data;
  },

  getTokenStats: async () => {
    const response = await api.get('/admin/tokens/stats');
    return response.data;
  },
};

const TokenAllocation: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [transactions, setTransactions] = useState<TokenTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  
  // Dialog states
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  
  // Form states
  const [tokenAmount, setTokenAmount] = useState(0);
  const [tokenReason, setTokenReason] = useState('');
  const [operationType, setOperationType] = useState<'add' | 'remove'>('add');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  // Mock data
  const mockUsers: User[] = [
    {
      id: 1,
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      tokenBalance: 1250,
      planName: 'Premium',
      monthlyAllocation: 1500,
      lastActivity: '2024-01-15T10:30:00Z',
      status: 'active',
      joinDate: '2023-06-15T00:00:00Z',
    },
    {
      id: 2,
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane.smith@example.com',
      tokenBalance: 850,
      planName: 'Essential',
      monthlyAllocation: 500,
      lastActivity: '2024-01-14T15:45:00Z',
      status: 'active',
      joinDate: '2023-08-22T00:00:00Z',
    },
    {
      id: 3,
      firstName: 'Mike',
      lastName: 'Johnson',
      email: 'mike.j@example.com',
      tokenBalance: 3200,
      planName: 'Ultimate',
      monthlyAllocation: 5000,
      lastActivity: '2024-01-15T09:15:00Z',
      status: 'active',
      joinDate: '2023-05-10T00:00:00Z',
    },
    {
      id: 4,
      firstName: 'Sarah',
      lastName: 'Wilson',
      email: 'sarah.w@example.com',
      tokenBalance: 95,
      planName: 'FREE',
      monthlyAllocation: 100,
      lastActivity: '2024-01-10T14:20:00Z',
      status: 'active',
      joinDate: '2023-12-01T00:00:00Z',
    },
    {
      id: 5,
      firstName: 'David',
      lastName: 'Brown',
      email: 'david.brown@example.com',
      tokenBalance: 0,
      planName: 'Essential',
      monthlyAllocation: 500,
      lastActivity: '2024-01-08T11:30:00Z',
      status: 'suspended',
      joinDate: '2023-09-15T00:00:00Z',
    },
  ];

  const mockTransactions: TokenTransaction[] = [
    {
      id: 1,
      userId: 1,
      userName: 'John Doe',
      type: 'allocation',
      amount: 1500,
      reason: 'Monthly allocation',
      adminId: 'admin-123',
      adminName: 'Admin User',
      timestamp: '2024-01-01T00:00:00Z',
      balanceBefore: 0,
      balanceAfter: 1500,
    },
    {
      id: 2,
      userId: 2,
      userName: 'Jane Smith',
      type: 'bonus',
      amount: 200,
      reason: 'Referral bonus',
      adminId: 'admin-123',
      adminName: 'Admin User',
      timestamp: '2024-01-05T10:30:00Z',
      balanceBefore: 650,
      balanceAfter: 850,
    },
    {
      id: 3,
      userId: 3,
      userName: 'Mike Johnson',
      type: 'deduction',
      amount: -100,
      reason: 'Benefit redemption',
      adminId: 'system',
      adminName: 'System',
      timestamp: '2024-01-10T14:15:00Z',
      balanceBefore: 3300,
      balanceAfter: 3200,
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch users and transactions from real API using centralized service
      const [usersResponse, transactionsResponse] = await Promise.allSettled([
        tokenService.getUsersWithTokens(),
        tokenService.getTokenHistory(),
      ]);

      if (usersResponse.status === 'fulfilled') {
        setUsers(usersResponse.value);
      } else {
        console.error('Failed to fetch users:', usersResponse.reason);
        setUsers(mockUsers);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load users data. Using demo data.', 
          severity: 'error' 
        });
      }

      if (transactionsResponse.status === 'fulfilled') {
        setTransactions(transactionsResponse.value);
      } else {
        console.error('Failed to fetch transactions:', transactionsResponse.reason);
        setTransactions(mockTransactions);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load transaction history. Using demo data.', 
          severity: 'error' 
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setUsers(mockUsers);
      setTransactions(mockTransactions);
      setSnackbar({ 
        open: true, 
        message: 'Failed to connect to server. Using demo data.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTokens = (user: User) => {
    setSelectedUser(user);
    setOperationType('add');
    setTokenAmount(0);
    setTokenReason('');
    setTokenDialogOpen(true);
  };

  const handleRemoveTokens = (user: User) => {
    setSelectedUser(user);
    setOperationType('remove');
    setTokenAmount(0);
    setTokenReason('');
    setTokenDialogOpen(true);
  };

  const handleSaveTokenOperation = async () => {
    if (!selectedUser || tokenAmount <= 0) return;

    try {
      setLoading(true);
      const finalAmount = operationType === 'remove' ? -tokenAmount : tokenAmount;
      
      // Use centralized API service
      await tokenService.updateUserTokens(selectedUser.id, finalAmount, tokenReason);

      // Refresh data to get updated balances
      await fetchData();

      setSnackbar({
        open: true,
        message: `Tokens ${operationType === 'add' ? 'added' : 'removed'} successfully`,
        severity: 'success',
      });

      setTokenDialogOpen(false);
      setTokenAmount(0);
      setTokenReason('');
    } catch (error) {
      console.error('Error updating tokens:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error updating tokens. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkOperation = async () => {
    if (selectedUsers.length === 0 || tokenAmount <= 0) return;

    try {
      setLoading(true);
      const finalAmount = operationType === 'remove' ? -tokenAmount : tokenAmount;
      
      // Process bulk operations using Promise.allSettled for error handling
      const bulkOperations = selectedUsers.map(userId => 
        tokenService.updateUserTokens(userId, finalAmount, tokenReason)
      );

      const results = await Promise.allSettled(bulkOperations);
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failureCount = results.filter(r => r.status === 'rejected').length;

      // Refresh data to get updated balances
      await fetchData();

      let message = '';
      let severity: 'success' | 'error' = 'success';

      if (failureCount === 0) {
        message = `Bulk ${operationType} operation completed successfully for ${successCount} users`;
      } else if (successCount === 0) {
        message = `Bulk operation failed for all ${failureCount} users`;
        severity = 'error';
      } else {
        message = `Bulk operation completed: ${successCount} successful, ${failureCount} failed`;
        severity = 'error';
      }

      setSnackbar({
        open: true,
        message,
        severity,
      });

      setBulkDialogOpen(false);
      setSelectedUsers([]);
      setTokenAmount(0);
      setTokenReason('');
    } catch (error) {
      console.error('Error with bulk operation:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error with bulk operation. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesPlan = planFilter.length === 0 || planFilter.includes(user.planName);
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(user.status);
    
    return matchesSearch && matchesPlan && matchesStatus;
  });

  const totalTokensAllocated = users.reduce((sum, user) => sum + user.tokenBalance, 0);
  const totalMonthlyAllocation = users.reduce((sum, user) => sum + user.monthlyAllocation, 0);
  const averageTokenBalance = users.length > 0 ? Math.round(totalTokensAllocated / users.length) : 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#4CAF50';
      case 'inactive': return '#FF9800';
      case 'suspended': return '#F44336';
      default: return '#757575';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'free': return '#4CAF50';
      case 'essential': return '#2196F3';
      case 'premium': return '#FF9800';
      case 'ultimate': return '#9C27B0';
      default: return '#757575';
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'allocation': return '#4CAF50';
      case 'bonus': return '#FFD700';
      case 'deduction': return '#F44336';
      case 'transfer': return '#2196F3';
      default: return '#757575';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
            Token Allocation Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#888' }}>
            Manage user token balances, allocations, and transaction history
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<TransferIcon />}
            onClick={() => setBulkDialogOpen(true)}
            sx={{ bgcolor: '#2196F3', '&:hover': { bgcolor: '#1976D2' } }}
          >
            Bulk Operations
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TokenIcon sx={{ color: '#FFD700', mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {totalTokensAllocated.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Total Tokens Allocated
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon sx={{ color: '#4CAF50', mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {totalMonthlyAllocation.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Monthly Allocation
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalanceIcon sx={{ color: '#2196F3', mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {averageTokenBalance.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Average Balance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <GroupIcon sx={{ color: '#FF9800', mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {users.filter(u => u.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Active Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            '& .MuiTab-root': { color: '#888' },
            '& .Mui-selected': { color: '#FFD700' },
          }}
        >
          <Tab label="User Token Balances" />
          <Tab label="Transaction History" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Filters */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <TextField
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#888', mr: 1 }} />,
              }}
              sx={{ 
                minWidth: 300,
                '& .MuiOutlinedInput-root': { color: 'white' },
              }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#888' }}>Plan</InputLabel>
              <Select
                multiple
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value as string[])}
                renderValue={(selected) => selected.join(', ')}
                label="Plan"
                sx={{ color: 'white' }}
              >
                {['FREE', 'Essential', 'Premium', 'Ultimate'].map((plan) => (
                  <MenuItem key={plan} value={plan}>
                    <Checkbox checked={planFilter.indexOf(plan) > -1} />
                    <ListItemText primary={plan} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: '#888' }}>Status</InputLabel>
              <Select
                multiple
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as string[])}
                renderValue={(selected) => selected.join(', ')}
                label="Status"
                sx={{ color: 'white' }}
              >
                {['active', 'inactive', 'suspended'].map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={statusFilter.indexOf(status) > -1} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Users Table */}
          <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#2A2A2A', color: 'white' } }}>
                  <TableCell>User</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Current Balance</TableCell>
                  <TableCell>Monthly Allocation</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => {
                  const usagePercentage = (user.tokenBalance / user.monthlyAllocation) * 100;
                  return (
                    <TableRow 
                      key={user.id} 
                      sx={{ 
                        '& .MuiTableCell-body': { 
                          color: 'white',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        } 
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar 
                            sx={{ 
                              bgcolor: getPlanColor(user.planName), 
                              mr: 2, 
                              width: 32, 
                              height: 32 
                            }}
                          >
                            {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#888' }}>
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.planName}
                          size="small"
                          sx={{ 
                            bgcolor: getPlanColor(user.planName), 
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
                          {user.tokenBalance.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.monthlyAllocation.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min(usagePercentage, 100)} 
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: usagePercentage > 90 ? '#F44336' : usagePercentage > 70 ? '#FF9800' : '#4CAF50',
                              },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#888' }}>
                            {usagePercentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.status}
                          size="small"
                          sx={{ 
                            bgcolor: getStatusColor(user.status), 
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#888' }}>
                          {formatDate(user.lastActivity)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Add Tokens">
                            <IconButton 
                              onClick={() => handleAddTokens(user)}
                              sx={{ color: '#4CAF50' }}
                              size="small"
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove Tokens">
                            <IconButton 
                              onClick={() => handleRemoveTokens(user)}
                              sx={{ color: '#F44336' }}
                              size="small"
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View History">
                            <IconButton sx={{ color: '#2196F3' }} size="small">
                              <HistoryIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Transaction History */}
          <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
              Recent Token Transactions
            </Typography>
            <Button
              startIcon={<ExportIcon />}
              sx={{ color: '#888' }}
            >
              Export History
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#2A2A2A', color: 'white' } }}>
                  <TableCell>Date</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Balance Change</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Admin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow 
                    key={transaction.id} 
                    sx={{ 
                      '& .MuiTableCell-body': { 
                        color: 'white',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      } 
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(transaction.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {transaction.userName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.type}
                        size="small"
                        sx={{ 
                          bgcolor: getTransactionTypeColor(transaction.type), 
                          color: 'white',
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: transaction.amount > 0 ? '#4CAF50' : '#F44336'
                        }}
                      >
                        {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#888' }}>
                        {transaction.balanceBefore.toLocaleString()} → {transaction.balanceAfter.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#888' }}>
                        {transaction.adminName}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Token Operation Dialog */}
      <Dialog
        open={tokenDialogOpen}
        onClose={() => setTokenDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1A1A1A',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          {operationType === 'add' ? 'Add Tokens' : 'Remove Tokens'} - {selectedUser?.firstName} {selectedUser?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
              Current Balance: <strong>{selectedUser?.tokenBalance.toLocaleString()} tokens</strong>
            </Typography>
            
            <TextField
              fullWidth
              label="Token Amount"
              type="number"
              value={tokenAmount}
              onChange={(e) => setTokenAmount(Math.max(0, Number(e.target.value)))}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': { color: 'white' },
                '& .MuiInputLabel-root': { color: '#888' },
              }}
            />
            
            <TextField
              fullWidth
              label="Reason"
              multiline
              rows={3}
              value={tokenReason}
              onChange={(e) => setTokenReason(e.target.value)}
              placeholder="Enter reason for token adjustment..."
              sx={{ 
                '& .MuiOutlinedInput-root': { color: 'white' },
                '& .MuiInputLabel-root': { color: '#888' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveTokenOperation}
            variant="contained"
            disabled={tokenAmount <= 0 || !tokenReason.trim()}
            sx={{ 
              bgcolor: operationType === 'add' ? '#4CAF50' : '#F44336',
              '&:hover': { 
                bgcolor: operationType === 'add' ? '#45A049' : '#D32F2F'
              }
            }}
          >
            {operationType === 'add' ? 'Add' : 'Remove'} Tokens
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Operations Dialog */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1A1A1A',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          Bulk Token Operations
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Select Users ({selectedUsers.length} selected)
            </Typography>
            
            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 3 }}>
              {users.map((user) => (
                <Box
                  key={user.id}
                  display="flex"
                  alignItems="center"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: selectedUsers.includes(user.id) ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setSelectedUsers(prev =>
                      prev.includes(user.id)
                        ? prev.filter(id => id !== user.id)
                        : [...prev, user.id]
                    );
                  }}
                >
                  <Checkbox
                    checked={selectedUsers.includes(user.id)}
                    sx={{ color: '#FFD700' }}
                  />
                  <Avatar sx={{ bgcolor: getPlanColor(user.planName), mr: 2, width: 32, height: 32 }}>
                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                      {user.email} • {user.tokenBalance.toLocaleString()} tokens
                    </Typography>
                  </Box>
                  <Chip 
                    label={user.planName}
                    size="small"
                    sx={{ bgcolor: getPlanColor(user.planName), color: 'white' }}
                  />
                </Box>
              ))}
            </Box>

            <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#888' }}>Operation</InputLabel>
                  <Select
                    value={operationType}
                    onChange={(e) => setOperationType(e.target.value as 'add' | 'remove')}
                    label="Operation"
                    sx={{ color: 'white' }}
                  >
                    <MenuItem value="add">Add Tokens</MenuItem>
                    <MenuItem value="remove">Remove Tokens</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Token Amount"
                  type="number"
                  value={tokenAmount}
                  onChange={(e) => setTokenAmount(Math.max(0, Number(e.target.value)))}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { color: 'white' },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason"
                  multiline
                  rows={3}
                  value={tokenReason}
                  onChange={(e) => setTokenReason(e.target.value)}
                  placeholder="Enter reason for bulk token adjustment..."
                  sx={{ 
                    '& .MuiOutlinedInput-root': { color: 'white' },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleBulkOperation}
            variant="contained"
            disabled={selectedUsers.length === 0 || tokenAmount <= 0 || !tokenReason.trim()}
            sx={{ 
              bgcolor: operationType === 'add' ? '#4CAF50' : '#F44336',
              '&:hover': { 
                bgcolor: operationType === 'add' ? '#45A049' : '#D32F2F'
              }
            }}
          >
            Apply to {selectedUsers.length} Users
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TokenAllocation;